import {
  Alert,
  BULK_EDIT_UPDATE_EMPTY_WARNING,
  Button,
  Card,
  Checkbox,
  Grid,
  GridBreak,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, DurationInput, FilePanel, Input, Select, TimePicker } from "components";
import {
  LkStudentServiceDisplayFieldId,
  StudentServiceBulkUpdate,
  StudentServiceBulkUpdateResource,
  StudentServiceBulkUpdateSchema,
  UploadFile,
} from "generated/openApi";
import ApiService from "services/apiService";
import { ServiceFiles } from "../ServiceFiles";
import { useState } from "react";
import { StudentSignature } from "./StudentSignature";
import { StaffSignature } from "./StaffSignature";
import { InfoIcon } from "components/common/InfoIcon";

export const ServiceUpdateSchema = (props: {
  updateSchema: StudentServiceBulkUpdateSchema;
  setUpdateSchema: (value: StudentServiceBulkUpdateSchema) => void;
  resource: StudentServiceBulkUpdateResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();

  const [studentSignatureOpen, setStudentSignatureOpen] = useState(false);
  const [staffSignatureOpen, setStaffSignatureOpen] = useState(false);

  const properties =
    props.resource.properties?.find((x) => x.propertyName === pn<StudentServiceBulkUpdate>("updateSchema"))?.children ||
    [];

  const selectedServiceType = props.updateSchema.updateServiceTypeId
    ? props.resource.serviceTypes?.find((x) => x.id === props.updateSchema.serviceTypeId)
    : undefined;

  const serviceSubTypes = props.updateSchema.serviceTypeId
    ? props.resource.serviceSubTypes?.filter((x) => x.serviceTypeId === props.updateSchema.serviceTypeId)
    : props.resource.serviceSubTypes;

  const displayFields = props.resource.displayFields || [];
  const durationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Duration);
  const startTimeDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.StartTime);
  const endTimeDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.EndTime);
  const serviceLocationDisplayField = displayFields.find(
    (x) => x.id === LkStudentServiceDisplayFieldId.ServiceLocation
  );
  const languageDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Language);
  const followUpDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.FollowUp);
  const studentSignatureDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Signature);
  const staffSignatureDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.StaffSignature);
  const nameDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Name);
  const modalityDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Modality);

  const dateField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("date");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDate;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDate: e.target.checked })}
        />

        <DatePicker
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.date}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, date: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateServiceTypeId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateServiceTypeId: e.target.checked })}
        />

        <Select
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.serviceTypes?.find((x) => x.id === props.updateSchema.serviceTypeId)}
          options={props.resource.serviceTypes}
          groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema({
              ...props.updateSchema,
              serviceTypeId: value?.id,
              serviceSubTypeId: undefined,
              parentCount: undefined,
            })
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateServiceSubTypeId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateServiceSubTypeId: e.target.checked })}
        />

        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={serviceSubTypes?.find((x) => x.id === props.updateSchema.serviceSubTypeId)}
          options={serviceSubTypes}
          groupBy={(option) => option.serviceTypeName}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, serviceSubTypeId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateParentCount;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateParentCount: e.target.checked })}
        />
        <Input
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.parentCount}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, parentCount: parseFloat(e.target.value) })}
          disabled={!isUpdating}
        />
        <InfoIcon text="Number of Parents Per Student" />
      </Stack>
    );
  };

  const nameField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("name");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateName;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateName: e.target.checked })}
        />
        <Select
          freeSolo
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          inputValue={props.updateSchema.name}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, name: value?.name })}
          onInputChange={(e) => props.setUpdateSchema({ ...props.updateSchema, name: e.target.value })}
          fetchFunction={(searchString: string) => ApiService.search.studentServiceNames(searchString)}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const durationInMinutesField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDurationInMinutes;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDurationInMinutes: e.target.checked })}
        />
        <DurationInput
          label={property?.displayName}
          required={isUpdating && durationDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.durationInMinutes}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, durationInMinutes: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const startTimeField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("startTime");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateStartTime;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateStartTime: e.target.checked })}
        />
        <TimePicker
          modelProperty={property}
          required={isUpdating && startTimeDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.startTime}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, startTime: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const endTimeField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("endTime");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEndTime;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEndTime: e.target.checked })}
        />
        <TimePicker
          modelProperty={property}
          required={isUpdating && endTimeDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.endTime}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, endTime: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const providersField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("providers");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateProviders;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateProviders: e.target.checked })}
        />
        <Select
          multiple
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.providers}
          options={props.resource.providers}
          getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, providers: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateSchoolId;
    const required =
      isUpdating &&
      selectedServiceType?.aprStudentService?.isServiceLocationForCollege !== true &&
      (selectedServiceType?.isServiceLocationMandatory || serviceLocationDisplayField?.required);

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateSchoolId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.schools?.find((x) => x.id === props.updateSchema.schoolId)}
          options={props.resource.schools}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, schoolId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const collegesField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateColleges;
    const required =
      isUpdating &&
      selectedServiceType?.aprStudentService?.isServiceLocationForCollege === true &&
      (selectedServiceType?.isServiceLocationMandatory || serviceLocationDisplayField?.required);

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateColleges: e.target.checked })}
        />
        <Select
          multiple
          modelProperty={property}
          required={required}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.colleges}
          options={props.resource.colleges}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, colleges: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const languageField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("languageId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateLanguageId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateLanguageId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={isUpdating && languageDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.languages?.find((x) => x.id === props.updateSchema.languageId)}
          options={props.resource.languages}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, languageId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const modalityField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("modalityId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateModalityId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateModalityId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.modalities?.find((x) => x.id === props.updateSchema.modalityId)}
          options={props.resource.modalities}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, modalityId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const noteField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("note");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateNote;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateNote: e.target.checked })}
        />
        <Input
          multiline
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.note}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, note: e.target.value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const followUpField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("followUp");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFollowUp;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateFollowUp: e.target.checked })}
        />
        <Input
          multiline
          modelProperty={property}
          required={isUpdating && followUpDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.followUp}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, followUp: e.target.value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const studentSignatureField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("studentSignatureUploadFile");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateStudentSignature;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateStudentSignature: e.target.checked })}
        />

        {props.updateSchema.studentSignatureUploadFile ? (
          <FilePanel
            fileName={props.updateSchema.studentSignatureUploadFile?.fileName}
            fileRef={props.updateSchema.studentSignatureUploadFile?.fileRef}
            fileSize={props.updateSchema.studentSignatureUploadFile?.fileSize}
            uploadingData={props.updateSchema.studentSignatureUploadFile?.uploadingData}
            imagePreviewData={props.updateSchema.studentSignatureUploadFile?.imagePreviewData}
            delete={() => props.setUpdateSchema({ ...props.updateSchema, studentSignatureUploadFile: undefined })}
            disabled={!isUpdating}
          />
        ) : (
          <Button onClick={() => setStudentSignatureOpen(true)} disabled={!isUpdating}>
            + Student Signature
          </Button>
        )}
      </Stack>
    );
  };

  const staffSignatureField = () => {
    const propertyName = pn<StudentServiceBulkUpdateSchema>("staffSignatureUploadFile");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateStaffSignature;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateStaffSignature: e.target.checked })}
        />

        {props.updateSchema.staffSignatureUploadFile ? (
          <Stack>
            <Typography variant="inherit">{props.updateSchema.staffName}</Typography>
            <FilePanel
              fileName={props.updateSchema.staffSignatureUploadFile?.fileName}
              fileRef={props.updateSchema.staffSignatureUploadFile?.fileRef}
              fileSize={props.updateSchema.staffSignatureUploadFile?.fileSize}
              uploadingData={props.updateSchema.staffSignatureUploadFile?.uploadingData}
              imagePreviewData={props.updateSchema.staffSignatureUploadFile?.imagePreviewData}
              delete={() => props.setUpdateSchema({ ...props.updateSchema, staffSignatureUploadFile: undefined })}
              disabled={!isUpdating}
            />
          </Stack>
        ) : (
          <Button onClick={() => setStaffSignatureOpen(true)} disabled={!isUpdating}>
            + Staff Signature
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Card header="Updating Fields">
      <Stack spacing="md">
        <Alert severity="warning" variant="standard">
          {BULK_EDIT_UPDATE_EMPTY_WARNING}
        </Alert>

        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {dateField()}
          </Grid>

          <GridBreak />

          <Grid item xs={smallFieldBreakpoints}>
            {serviceTypeField()}
          </Grid>
          <Grid item xs={smallFieldBreakpoints}>
            {serviceSubTypeField()}
          </Grid>
          <Grid item xs={smallFieldBreakpoints}>
            {parentCountField()}
          </Grid>
          {nameDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {nameField()}
            </Grid>
          )}
          {durationDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {durationInMinutesField()}
            </Grid>
          )}
          {startTimeDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {startTimeField()}
            </Grid>
          )}
          {endTimeDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {endTimeField()}
            </Grid>
          )}
          {languageDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {languageField()}
            </Grid>
          )}

          {modalityDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {modalityField()}
            </Grid>
          )}

          {serviceLocationDisplayField && (
            <>
              {selectedServiceType?.aprStudentService?.isServiceLocationForCollege ? (
                <Grid item xs={largeFieldBreakpoints}>
                  {collegesField()}
                </Grid>
              ) : (
                <Grid item xs={smallFieldBreakpoints}>
                  {schoolField()}
                </Grid>
              )}
            </>
          )}

          <Grid item xs={largeFieldBreakpoints}>
            {providersField()}
          </Grid>

          <GridBreak />

          <Grid item xs={largeFieldBreakpoints}>
            {noteField()}
          </Grid>

          {followUpDisplayField && (
            <Grid item xs={largeFieldBreakpoints}>
              {followUpField()}
            </Grid>
          )}

          {studentSignatureDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {studentSignatureField()}
            </Grid>
          )}

          {staffSignatureDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {staffSignatureField()}
            </Grid>
          )}

          <Grid item xs={fullFieldBreakpoints}>
            <ServiceFiles
              files={props.updateSchema.creatingFiles || []}
              setFiles={(files) => props.setUpdateSchema({ ...props.updateSchema, creatingFiles: files })}
            />
          </Grid>
        </Grid>
      </Stack>

      <StudentSignature
        open={studentSignatureOpen}
        close={() => setStudentSignatureOpen(false)}
        signatureFile={props.updateSchema.studentSignatureUploadFile || ({} as UploadFile)}
        done={(value) => {
          props.setUpdateSchema({ ...props.updateSchema, studentSignatureUploadFile: value });
          setStudentSignatureOpen(false);
        }}
      />

      <StaffSignature
        open={staffSignatureOpen}
        close={() => setStaffSignatureOpen(false)}
        service={props.updateSchema || ({} as StudentServiceBulkUpdateSchema)}
        done={(value) => {
          props.setUpdateSchema(value);
          setStaffSignatureOpen(false);
        }}
      />
    </Card>
  );
};
